import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Upload, Plus, Search, Building2, TrendingUp, TrendingDown, Loader2, Trash2, FileUp, ArrowRight,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtCompact, fmtNum, RATING_META } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const RISK = {
  basso: { label: "Rischio Basso", cls: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10" },
  medio: { label: "Rischio Medio", cls: "text-amber-500 border-amber-500/40 bg-amber-500/10" },
  alto: { label: "Rischio Alto", cls: "text-red-500 border-red-500/40 bg-red-500/10" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSector, setNewSector] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/upload-xbrl", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`XBRL elaborato: esercizi ${data.years.join(", ")}`);
      load();
      setTimeout(() => navigate(`/azienda/${data.company_id}`), 600);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const createCompany = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/companies", { name: newName, sector: newSector });
      toast.success("Azienda creata");
      setCreateOpen(false);
      setNewName(""); setNewSector("");
      navigate(`/azienda/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Azienda eliminata");
      setCompanies((c) => c.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = companies.filter(
    (c) => c.name.toLowerCase().includes(query.toLowerCase()) ||
      (c.sector || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Portfolio Clienti</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Le tue Aziende</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Carica un bilancio XBRL o seleziona un cliente per l'analisi completa.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".xbrl,.xml,.zip" className="hidden" onChange={handleUpload} data-testid="xbrl-file-input" />
          <Button variant="outline" data-testid="upload-xbrl-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Carica XBRL
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-company-btn"><Plus className="h-4 w-4 mr-2" /> Nuova Azienda</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuova Azienda</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="cn">Denominazione</Label>
                  <Input id="cn" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Es. Alfa S.r.l." data-testid="new-company-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cs">Settore</Label>
                  <Input id="cs" value={newSector} onChange={(e) => setNewSector(e.target.value)} placeholder="Es. Manifatturiero" data-testid="new-company-sector-input" />
                </div>
                <p className="text-xs text-muted-foreground">Dopo la creazione potrai caricare i bilanci XBRL dalla scheda azienda.</p>
              </div>
              <DialogFooter>
                <Button onClick={createCompany} data-testid="new-company-save-btn">Crea</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9" placeholder="Cerca per nome o settore..." value={query}
          onChange={(e) => setQuery(e.target.value)} data-testid="company-search-input"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <FileUp className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading font-semibold text-lg">Nessuna azienda</h3>
          <p className="text-sm text-muted-foreground mt-1">Carica un file XBRL per iniziare l'analisi.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filtered.map((c, i) => {
            const k = c.kpi || {};
            const risk = RISK[k.rischio_crisi] || RISK.basso;
            const marginPos = (k.ebitda_margin ?? 0) >= 8;
            return (
              <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card
                  data-testid={`company-card-${c.id}`}
                  onClick={() => navigate(`/azienda/${c.id}`)}
                  className="p-5 cursor-pointer hover:border-primary/50 hover:shadow-md transition-colors duration-150 group h-full flex flex-col"
                >
                  <div className="flex items-start justify-between">
                    <div className="grid place-items-center h-10 w-10 rounded-lg bg-primary/10 text-primary">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {k.rating && (
                        <span
                          data-testid={`company-rating-${c.id}`}
                          title={`Rating ${k.rating} · ${k.rating_score}/100 · ${k.rating_label}`}
                          className={`grid place-items-center h-6 w-6 rounded-md border font-heading font-bold text-sm ${(RATING_META[k.rating] || RATING_META.D).text} ${(RATING_META[k.rating] || RATING_META.D).box}`}
                        >{k.rating}</span>
                      )}
                      <Badge variant="outline" className={`text-[10px] font-mono ${risk.cls}`}>{risk.label}</Badge>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-company-${c.id}`} className="text-muted-foreground hover:text-red-500 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare {c.name}?</AlertDialogTitle>
                            <AlertDialogDescription>L'azienda e tutti i bilanci associati verranno rimossi definitivamente.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(c.id)} data-testid={`confirm-delete-${c.id}`}>Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  <h3 className="font-heading font-semibold text-lg mt-4 tracking-tight leading-snug">{c.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.sector || "Settore non indicato"}</p>

                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fatturato</div>
                      <div className="font-mono font-semibold text-base mt-0.5">{fmtCompact(k.fatturato)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">EBITDA Margin</div>
                      <div className={`font-mono font-semibold text-base mt-0.5 flex items-center gap-1 ${marginPos ? "text-emerald-500" : "text-amber-500"}`}>
                        {marginPos ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                        {fmtNum(k.ebitda_margin, 1)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">PFN</div>
                      <div className="font-mono font-semibold text-base mt-0.5">{fmtCompact(k.pfn)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Patrimonio Netto</div>
                      <div className="font-mono font-semibold text-base mt-0.5">{fmtCompact(k.patrimonio_netto)}</div>
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-border/60 flex items-center justify-between">
                    <div className="flex gap-1">
                      {(c.years || []).map((y) => (
                        <span key={y} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground">{y}</span>
                      ))}
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
